import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, CheckCircle2, Eye, FilePenLine, FileText, Plus, RotateCcw, Trash2, Upload } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { LegalMarkdownContent } from '../components/LegalDocumentContent';
import { useNotification } from '../components/NotificationProvider';
import {
  AdminLegalDocument,
  LegalDocumentDraftRequest,
  LegalDocumentType,
  createLegalDocumentDraft,
  deleteLegalDocumentDraft,
  fetchAdminLegalDocuments,
  publishLegalDocument,
  returnLegalDocumentToDraft,
  updateLegalDocumentDraft,
} from '../services/admin.api';
import { invalidateLegalDocumentCache } from '../services/legalDocuments';
import { ApiError } from '../services/api.error';
import { useTranslations } from '../translations';
import './AdminLegalDocuments.css';

type EditorMode = 'edit' | 'preview';
type DraftField = 'documentVersion' | 'title' | 'effectiveAt' | 'changeSummary' | 'content';
type DraftErrors = Partial<Record<DraftField, string>>;

const toLocalDateTime = (date: Date): string => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const documentTypeDefaults: Record<LegalDocumentType, { title: string; requiresAcceptance: boolean }> = {
  TERMS: { title: 'Termeni și Condiții – roade.ro', requiresAcceptance: true },
  PRIVACY: { title: 'Politica de Confidențialitate – roade.ro (GDPR)', requiresAcceptance: false },
  COOKIES: { title: 'Politica privind cookie-urile – roade.ro', requiresAcceptance: false },
};

const documentTypes = Object.keys(documentTypeDefaults) as LegalDocumentType[];

const emptyDraft = (documentType: LegalDocumentType = 'TERMS'): LegalDocumentDraftRequest => ({
  documentType,
  documentVersion: new Date().toISOString().slice(0, 10),
  title: documentTypeDefaults[documentType].title,
  content: '',
  effectiveAt: toLocalDateTime(new Date()),
  requiresAcceptance: documentTypeDefaults[documentType].requiresAcceptance,
  changeSummary: '',
});

const documentTypeLabels: Record<LegalDocumentType, string> = {
  TERMS: 'Termeni și condiții',
  PRIVACY: 'Politica de confidențialitate',
  COOKIES: 'Politica privind cookie-urile',
};

const statusLabels: Record<AdminLegalDocument['status'], string> = {
  DRAFT: 'Ciornă',
  SCHEDULED: 'Programat',
  ACTIVE: 'Activ',
  ARCHIVED: 'Arhivat',
};

const AdminLegalDocuments: React.FC = () => {
  const t = useTranslations();
  const copy = t.administration.legalDocuments;
  const { showNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<AdminLegalDocument[]>([]);
  const [activeDocumentType, setActiveDocumentType] = useState<LegalDocumentType>('TERMS');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<LegalDocumentDraftRequest>(emptyDraft);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('edit');
  const [readOnlyPreview, setReadOnlyPreview] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<DraftErrors>({});
  const [confirmAction, setConfirmAction] = useState<{
    type: 'publish' | 'delete';
    document: AdminLegalDocument;
  } | null>(null);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      setDocuments(await fetchAdminLegalDocuments());
    } catch (error) {
      console.error('Error loading legal documents:', error);
      showNotification(copy.loadError, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDocuments();
  }, []);

  const activeDocuments = useMemo(
    () => documents.filter((document) => document.documentType === activeDocumentType),
    [activeDocumentType, documents],
  );

  const documentCounts = useMemo(() => documentTypes.reduce<Record<LegalDocumentType, number>>(
    (counts, type) => ({ ...counts, [type]: documents.filter((document) => document.documentType === type).length }),
    { TERMS: 0, PRIVACY: 0, COOKIES: 0 },
  ), [documents]);

  const selectDocumentType = (documentType: LegalDocumentType) => {
    setActiveDocumentType(documentType);
    setEditorOpen(false);
    setEditingId(null);
    setFieldErrors({});
  };

  const startCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft(activeDocumentType));
    setFieldErrors({});
    setEditorMode('edit');
    setReadOnlyPreview(false);
    setEditorOpen(true);
  };

  const startEdit = (document: AdminLegalDocument) => {
    setActiveDocumentType(document.documentType);
    setEditingId(document.id);
    setDraft({
      documentType: document.documentType,
      documentVersion: document.documentVersion,
      title: document.title,
      content: document.content,
      effectiveAt: toLocalDateTime(new Date(document.effectiveAt)),
      requiresAcceptance: document.requiresAcceptance,
      changeSummary: document.changeSummary ?? '',
    });
    setEditorMode('edit');
    setReadOnlyPreview(false);
    setFieldErrors({});
    setEditorOpen(true);
  };

  const previewDocument = (document: AdminLegalDocument) => {
    setActiveDocumentType(document.documentType);
    setEditingId(null);
    setDraft({
      documentType: document.documentType,
      documentVersion: document.documentVersion,
      title: document.title,
      content: document.content,
      effectiveAt: toLocalDateTime(new Date(document.effectiveAt)),
      requiresAcceptance: document.requiresAcceptance,
      changeSummary: document.changeSummary ?? '',
    });
    setEditorMode('preview');
    setReadOnlyPreview(true);
    setFieldErrors({});
    setEditorOpen(true);
  };

  const updateDraftField = (field: DraftField, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const focusFirstInvalidField = (errors: DraftErrors) => {
    const firstField = (Object.keys(errors) as DraftField[])[0];
    if (firstField) requestAnimationFrame(() => document.getElementById(`legal-${firstField}`)?.focus());
  };

  const validateDraft = (): DraftErrors => {
    const errors: DraftErrors = {};
    if (!draft.documentVersion.trim()) errors.documentVersion = copy.fieldRequired;
    else if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(draft.documentVersion.trim())) {
      errors.documentVersion = copy.versionFormat;
    }
    if (!draft.title.trim()) errors.title = copy.fieldRequired;
    if (!draft.effectiveAt) errors.effectiveAt = copy.fieldRequired;
    else if (Number.isNaN(new Date(draft.effectiveAt).getTime())) errors.effectiveAt = copy.invalidDate;
    if (!draft.changeSummary?.trim()) errors.changeSummary = copy.fieldRequired;
    if (!draft.content.trim()) errors.content = copy.fieldRequired;
    return errors;
  };

  const applyApiFieldErrors = (error: ApiError): DraftErrors => {
    const errors: DraftErrors = {};
    error.errors.forEach((backendError) => {
      const field = backendError.field as DraftField | undefined;
      if (!field || !['documentVersion', 'title', 'effectiveAt', 'changeSummary', 'content'].includes(field)) return;
      if (backendError.code === 'NotBlank' || backendError.code === 'NotNull') {
        errors[field] = copy.fieldRequired;
      } else if (backendError.code === 'Pattern' && field === 'documentVersion') {
        errors[field] = copy.versionFormat;
      } else {
        errors[field] = copy.invalidField;
      }
    });
    return errors;
  };

  const legalApiErrorMessage = (error: unknown, fallback: string) => {
    if (!(error instanceof ApiError)) return fallback;
    if (error.hasErrorCode('legal_document_version_exists')) return copy.versionExistsError;
    if (error.hasErrorCode('legal_document_acceptance_required')) return copy.termsAcceptanceError;
    if (error.hasErrorCode('legal_document_acceptance_not_supported')) return copy.acceptanceNotSupportedError;
    if (error.hasErrorCode('legal_document_not_found')) return copy.notFoundError;
    if (error.hasErrorCode('legal_document_invalid_status')) return copy.invalidStatusError;
    return fallback;
  };

  const saveDraft = async () => {
    const validationErrors = validateDraft();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      focusFirstInvalidField(validationErrors);
      showNotification(copy.requiredFields, 'error');
      return;
    }

    setBusy(true);
    try {
      const request = {
        ...draft,
        requiresAcceptance: draft.documentType === 'TERMS',
        effectiveAt: new Date(draft.effectiveAt).toISOString(),
      };
      if (editingId) await updateLegalDocumentDraft(editingId, request);
      else await createLegalDocumentDraft(request);
      showNotification(copy.saveSuccess, 'success');
      setEditorOpen(false);
      await loadDocuments();
    } catch (error) {
      console.error('Error saving legal document draft:', error);
      if (error instanceof ApiError) {
        const apiFieldErrors = applyApiFieldErrors(error);
        if (error.hasErrorCode('legal_document_version_exists')) {
          apiFieldErrors.documentVersion = copy.versionExistsError;
        }
        if (Object.keys(apiFieldErrors).length > 0) {
          setFieldErrors(apiFieldErrors);
          focusFirstInvalidField(apiFieldErrors);
        }
      }
      showNotification(legalApiErrorMessage(error, copy.saveError), 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmPublishOrDelete = async () => {
    if (!confirmAction) return;
    setBusy(true);
    try {
      if (confirmAction.type === 'publish') {
        const updated = await publishLegalDocument(confirmAction.document.id);
        invalidateLegalDocumentCache(updated.documentType);
        showNotification(updated.status === 'SCHEDULED' ? copy.scheduleSuccess : copy.publishSuccess, 'success');
      } else {
        await deleteLegalDocumentDraft(confirmAction.document.id);
        showNotification(copy.deleteSuccess, 'success');
      }
      setConfirmAction(null);
      await loadDocuments();
    } catch (error) {
      console.error('Error changing legal document:', error);
      showNotification(legalApiErrorMessage(error, copy.actionError), 'error');
    } finally {
      setBusy(false);
    }
  };

  const undoSchedule = async (document: AdminLegalDocument) => {
    setBusy(true);
    try {
      await returnLegalDocumentToDraft(document.id);
      showNotification(copy.returnSuccess, 'success');
      await loadDocuments();
    } catch (error) {
      console.error('Error returning legal document to draft:', error);
      showNotification(legalApiErrorMessage(error, copy.actionError), 'error');
    } finally {
      setBusy(false);
    }
  };

  const importTextFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!/\.(md|txt)$/i.test(file.name)) {
      showNotification(copy.fileTypeError, 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateDraftField('content', String(reader.result ?? ''));
    reader.onerror = () => showNotification(copy.fileReadError, 'error');
    reader.readAsText(file, 'UTF-8');
  };

  const formatDate = (value: string) => new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

  return (
    <section className="admin-legal-documents">
      <header className="admin-legal-header">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
        <button type="button" className="admin-legal-primary" onClick={startCreate}>
          <Plus aria-hidden />
          {copy.newVersion}
        </button>
      </header>

      <p className="admin-legal-empty">Programarea unei versiuni nu trimite emailuri. Pentru preaviz, programează noii Termeni din timp, apoi pregătește și trimite mesajul din „Informări prin email”.</p>

      <div className="admin-legal-tabs" role="tablist" aria-label={copy.documentTabs}>
        {documentTypes.map((type) => (
          <button
            key={type}
            id={`legal-tab-${type.toLowerCase()}`}
            type="button"
            role="tab"
            aria-selected={activeDocumentType === type}
            aria-controls={`legal-panel-${type.toLowerCase()}`}
            className={activeDocumentType === type ? 'active' : ''}
            onClick={() => selectDocumentType(type)}
          >
            <FileText aria-hidden />
            <span>{documentTypeLabels[type]}</span>
            <span className="admin-legal-tab-count">{documentCounts[type]}</span>
          </button>
        ))}
      </div>

      {editorOpen && (
        <div className="admin-legal-editor">
          <div className="admin-legal-editor-heading">
            <div>
              <h3>{editingId ? copy.editDraft : copy.createDraft}</h3>
              <p>{documentTypeLabels[draft.documentType]} · {copy.immutableHint}</p>
            </div>
            {!readOnlyPreview && <div className="admin-legal-mode" aria-label={copy.viewMode}>
              <button type="button" className={editorMode === 'edit' ? 'active' : ''} onClick={() => setEditorMode('edit')}>
                <FilePenLine aria-hidden />{copy.edit}
              </button>
              <button type="button" className={editorMode === 'preview' ? 'active' : ''} onClick={() => setEditorMode('preview')}>
                <Eye aria-hidden />{copy.preview}
              </button>
            </div>}
          </div>

          {editorMode === 'edit' ? (
            <div className="admin-legal-form">
              <label className={fieldErrors.documentVersion ? 'admin-legal-field-error' : ''}>
                <span>{copy.version}<b aria-hidden>*</b></span>
                <input id="legal-documentVersion" required aria-invalid={Boolean(fieldErrors.documentVersion)} aria-describedby={fieldErrors.documentVersion ? 'legal-documentVersion-error' : undefined} value={draft.documentVersion} disabled={Boolean(editingId)} maxLength={64} onChange={(event) => updateDraftField('documentVersion', event.target.value)} />
                {fieldErrors.documentVersion && <small id="legal-documentVersion-error" role="alert">{fieldErrors.documentVersion}</small>}
              </label>
              <label className={fieldErrors.effectiveAt ? 'admin-legal-field-error' : ''}>
                <span>{copy.effectiveAt}<b aria-hidden>*</b></span>
                <input id="legal-effectiveAt" required aria-invalid={Boolean(fieldErrors.effectiveAt)} aria-describedby={fieldErrors.effectiveAt ? 'legal-effectiveAt-error' : undefined} type="datetime-local" value={draft.effectiveAt} onChange={(event) => updateDraftField('effectiveAt', event.target.value)} />
                {fieldErrors.effectiveAt && <small id="legal-effectiveAt-error" role="alert">{fieldErrors.effectiveAt}</small>}
              </label>
              <label className={`admin-legal-wide ${fieldErrors.title ? 'admin-legal-field-error' : ''}`}>
                <span>{copy.documentTitle}<b aria-hidden>*</b></span>
                <input id="legal-title" required aria-invalid={Boolean(fieldErrors.title)} aria-describedby={fieldErrors.title ? 'legal-title-error' : undefined} value={draft.title} maxLength={255} onChange={(event) => updateDraftField('title', event.target.value)} />
                {fieldErrors.title && <small id="legal-title-error" role="alert">{fieldErrors.title}</small>}
              </label>
              {draft.documentType === 'TERMS' && (
                <div className="admin-legal-acceptance-note admin-legal-wide">
                  <CheckCircle2 aria-hidden />
                  <div><strong>{copy.acceptanceRequired}</strong><span>{copy.termsAcceptanceHint}</span></div>
                </div>
              )}
              <label className={`admin-legal-wide ${fieldErrors.changeSummary ? 'admin-legal-field-error' : ''}`}>
                <span>{copy.changeSummary}<b aria-hidden>*</b></span>
                <input id="legal-changeSummary" required aria-invalid={Boolean(fieldErrors.changeSummary)} aria-describedby={fieldErrors.changeSummary ? 'legal-changeSummary-error' : undefined} value={draft.changeSummary ?? ''} maxLength={2000} onChange={(event) => updateDraftField('changeSummary', event.target.value)} />
                {fieldErrors.changeSummary && <small id="legal-changeSummary-error" role="alert">{fieldErrors.changeSummary}</small>}
              </label>
              <label className={`admin-legal-wide admin-legal-content-field ${fieldErrors.content ? 'admin-legal-field-error' : ''}`}>
                <span>{copy.content}<b aria-hidden>*</b></span>
                <textarea id="legal-content" required aria-invalid={Boolean(fieldErrors.content)} aria-describedby={fieldErrors.content ? 'legal-content-error' : undefined} value={draft.content} maxLength={500000} onChange={(event) => updateDraftField('content', event.target.value)} />
                {fieldErrors.content && <small id="legal-content-error" role="alert">{fieldErrors.content}</small>}
              </label>
              <input ref={fileInputRef} className="admin-legal-file-input" type="file" accept=".md,.txt,text/markdown,text/plain" onChange={importTextFile} />
              <button type="button" className="admin-legal-secondary admin-legal-import" onClick={() => fileInputRef.current?.click()}>
                <Upload aria-hidden />{copy.importFile}
              </button>
            </div>
          ) : (
            <article className="admin-legal-preview legal-container">
              <h1 className="legal-title">{draft.title || copy.untitled}</h1>
              <p className="legal-update">{copy.effectiveAt}: {draft.effectiveAt ? formatDate(new Date(draft.effectiveAt).toISOString()) : '-'}</p>
              {draft.content ? <LegalMarkdownContent content={draft.content} /> : <p>{copy.emptyPreview}</p>}
            </article>
          )}

          <div className="admin-legal-editor-actions">
            <button type="button" className="admin-legal-secondary" disabled={busy} onClick={() => setEditorOpen(false)}>{readOnlyPreview ? copy.close : copy.cancel}</button>
            {!readOnlyPreview && <button type="button" className="admin-legal-primary" disabled={busy} onClick={() => void saveDraft()}>{busy ? copy.saving : copy.saveDraft}</button>}
          </div>
        </div>
      )}

      {loading ? <div className="admin-legal-state">{t.common.loading}</div> : (
        <div
          id={`legal-panel-${activeDocumentType.toLowerCase()}`}
          className="admin-legal-groups"
          role="tabpanel"
          aria-labelledby={`legal-tab-${activeDocumentType.toLowerCase()}`}
        >
            <section className="admin-legal-group">
              {activeDocuments.length === 0 ? <p className="admin-legal-empty">{copy.noVersions}</p> : (
                <div className="admin-legal-list">
                  {activeDocuments.map((document) => (
                    <article className="admin-legal-row" key={document.id}>
                      <div className="admin-legal-row-main">
                        <div className="admin-legal-row-title">
                          <strong>{document.documentVersion}</strong>
                          <span className={`admin-legal-status status-${document.status.toLowerCase()}`}>{statusLabels[document.status]}</span>
                          {document.requiresAcceptance && <span className="admin-legal-acceptance-badge">{copy.acceptanceRequired}</span>}
                        </div>
                        <span>{document.title}</span>
                        {document.changeSummary && <p>{document.changeSummary}</p>}
                      </div>
                      <dl className="admin-legal-meta">
                        <div><dt>{copy.effectiveAt}</dt><dd>{formatDate(document.effectiveAt)}</dd></div>
                        {document.documentType === 'TERMS' && <div><dt>{copy.acceptances}</dt><dd>{document.acceptanceCount}</dd></div>}
                      </dl>
                      <div className="admin-legal-row-actions">
                        <button type="button" className="admin-legal-icon-button" title={copy.preview} aria-label={`${copy.preview} ${document.documentVersion}`} onClick={() => previewDocument(document)}><Eye aria-hidden /></button>
                        {document.status === 'DRAFT' && <>
                          <button type="button" className="admin-legal-icon-button" title={copy.editDraft} aria-label={copy.editDraft} disabled={busy} onClick={() => startEdit(document)}><FilePenLine aria-hidden /></button>
                          <button type="button" className="admin-legal-secondary" disabled={busy} onClick={() => setConfirmAction({ type: 'publish', document })}><CalendarClock aria-hidden />{new Date(document.effectiveAt) > new Date() ? copy.schedule : copy.publish}</button>
                          <button type="button" className="admin-legal-icon-button danger" title={copy.deleteDraft} aria-label={copy.deleteDraft} disabled={busy} onClick={() => setConfirmAction({ type: 'delete', document })}><Trash2 aria-hidden /></button>
                        </>}
                        {document.status === 'SCHEDULED' && <button type="button" className="admin-legal-secondary" disabled={busy} onClick={() => void undoSchedule(document)}><RotateCcw aria-hidden />{copy.returnToDraft}</button>}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.type === 'publish' ? copy.publishConfirmTitle : copy.deleteConfirmTitle}
          message={confirmAction.type === 'publish'
            ? copy.publishConfirmMessage.replace('{version}', confirmAction.document.documentVersion)
            : copy.deleteConfirmMessage.replace('{version}', confirmAction.document.documentVersion)}
          confirmText={confirmAction.type === 'publish' ? copy.confirmPublish : copy.confirmDelete}
          destructive={confirmAction.type === 'delete'}
          isConfirming={busy}
          onConfirm={() => void confirmPublishOrDelete()}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </section>
  );
};

export default AdminLegalDocuments;
